import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { LogOut, Shield, Users, Eye, Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import Logo from '../ui/Logo';
import { useNavigate } from 'react-router-dom';

interface UserForReview {
  id: string;
  email: string;
  displayName: string;
  photoURL?: string;
  role: string;
  managers?: Array<{
    id: string;
    name: string;
    email: string;
  }>;
  reviewCycle?: string;
  hasCompletedReview?: boolean;
}

const Dashboard: React.FC = () => {
  const { currentUser, logout, userProfile } = useAuth();
  const navigate = useNavigate();
  const [usersForReview, setUsersForReview] = useState<UserForReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCurrentUserUpForPulseCheck, setIsCurrentUserUpForPulseCheck] = useState(false);
  const [pulseCheckCompleted, setPulseCheckCompleted] = useState(false);
  const [pulseCheckCompletedDate, setPulseCheckCompletedDate] = useState<Date | null>(null);

  // Check if current user is superadmin, admin, HR, or manager
  const superAdmins = ['hunter@stitchdesignco.com', 'laine@stitchdesignco.com'];
  const isSuperAdminUser = currentUser?.email && superAdmins.includes(currentUser.email.toLowerCase());
  const isAdmin = isSuperAdminUser || userProfile?.role === 'admin';
  const isHR = userProfile?.isHR === true;
  const [userRole, setUserRole] = useState<string>('employee');

  useEffect(() => {
    const fetchUsersForReview = async () => {
      try {
        if (!currentUser) return;
        
        // Reset pulse check state at the start of each fetch
        setPulseCheckCompleted(false);
        setPulseCheckCompletedDate(null);
        
        // Fetch users who are currently up for review from Firestore
        const { collection, doc, getDocFromServer, getDocs, query, where } = await import('firebase/firestore');
        const { db } = await import('../../lib/firebase');
        
        // Get current user's role and check if they're up for review
        // Use getDocFromServer to bypass cache and get fresh data
        const currentUserRef = doc(db, 'users', currentUser.uid);
        const currentUserDoc = await getDocFromServer(currentUserRef);
        const currentUserData = currentUserDoc.exists() ? currentUserDoc.data() : {};
        
        setUserRole(currentUserData.role || 'employee');
        setIsCurrentUserUpForPulseCheck(currentUserData.upForPulseCheck || false);
        
        // Check if user has completed their engagement pulse check for the CURRENT pulse check cycle
        // Use pulseCheckCycleStart (timestamp) for accurate comparison, fall back to reviewCycleStart for backward compatibility
        let pulseCheckCycleStart: Date | null = null;
        if (currentUserData.pulseCheckCycleStart?.toDate) {
          pulseCheckCycleStart = currentUserData.pulseCheckCycleStart.toDate();
        } else if (currentUserData.reviewCycleStart?.toDate) {
          // Backward compatibility: use review cycle start if pulse check cycle not set
          pulseCheckCycleStart = currentUserData.reviewCycleStart.toDate();
        } else if (currentUserData.reviewCycle) {
          // Parse as local date (append T00:00:00 to avoid UTC interpretation)
          pulseCheckCycleStart = new Date(currentUserData.reviewCycle + 'T00:00:00');
        }
        
        const pulseSubmissionsRef = collection(db, 'engagementPulseSubmissions');
        const pulseQuery = query(
          pulseSubmissionsRef,
          where('userId', '==', currentUser.uid),
          where('status', '==', 'completed')
        );
        const pulseSnapshot = await getDocs(pulseQuery);
        
        if (!pulseSnapshot.empty) {
          // Find if there's a submission for the current pulse check cycle
          let hasCurrentCycleSubmission = false;
          let currentCycleSubmissionDate: Date | null = null;
          
          for (const docSnap of pulseSnapshot.docs) {
            const submissionData = docSnap.data();
            const submittedAt = submissionData.submittedAt?.toDate();
            
            // If no pulse check cycle set, treat any submission as complete
            if (!pulseCheckCycleStart) {
              hasCurrentCycleSubmission = true;
              currentCycleSubmissionDate = submittedAt;
              break;
            }
            
            // Check if this submission was made AFTER the current pulse check cycle started
            if (submittedAt && submittedAt > pulseCheckCycleStart) {
              hasCurrentCycleSubmission = true;
              currentCycleSubmissionDate = submittedAt;
              break;
            }
          }
          
          if (hasCurrentCycleSubmission) {
            setPulseCheckCompleted(true);
            if (currentCycleSubmissionDate) {
              setPulseCheckCompletedDate(currentCycleSubmissionDate);
            }
          }
        }
        
        const usersRef = collection(db, 'users');
        const reviewQuery = query(usersRef, where('upForReview', '==', true));
        const snapshot = await getDocs(reviewQuery);
        
        // Check which users have completed reviews by current user
        const reviewsRef = collection(db, 'reviewSubmissions');
        const completedQuery = query(
          reviewsRef, 
          where('reviewerId', '==', currentUser.uid),
          where('status', '==', 'completed')
        );
        const completedSnapshot = await getDocs(completedQuery);
        const completedRevieweeIds = new Set(
          completedSnapshot.docs.map(doc => doc.data().revieweeId)
        );

        const reviewUsers: UserForReview[] = snapshot.docs
          .filter(doc => doc.id !== currentUser.uid) // Prevent users from reviewing themselves
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              email: data.email || '',
              displayName: data.displayName || data.email?.split('@')[0] || 'Unknown',
              photoURL: data.photoURL || undefined,
              role: data.role || 'employee',
              managers: data.managers || [],
              reviewCycle: data.reviewCycle,
              hasCompletedReview: completedRevieweeIds.has(doc.id)
            };
          });
        
        // Sort by display name for consistent ordering
        reviewUsers.sort((a, b) => a.displayName.localeCompare(b.displayName));
        
        setUsersForReview(reviewUsers);
        console.log(`Dashboard loaded: ${reviewUsers.length} users up for review`);
      } catch (err: any) {
        console.error('Error loading users for review:', err);
        setError('Failed to load users for review');
      } finally {
        setLoading(false);
      }
    };

    fetchUsersForReview();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleStartReview = (userId: string) => {
    // Navigate to the review page for this user
    navigate(`/review/${userId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Logo className="text-primary" width={120} height={60} />
            <div>
              <h1 className="text-2xl font-bold text-primary">Professional Pathways</h1>
              <p className="text-muted-foreground">Welcome back, {currentUser?.displayName || currentUser?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(isAdmin || isHR || userRole === 'manager') && (
              <Button variant="secondary" onClick={() => navigate('/admin/reviews')} className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                View Team Reviews
              </Button>
            )}
            {(isAdmin || isHR || userRole === 'manager') && (
              <Button variant="secondary" onClick={() => navigate('/team/pulse-responses')} className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Team Pulse Responses
              </Button>
            )}
            {(isAdmin || isHR) ? (
              <Button variant="secondary" onClick={() => navigate('/admin')} className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Admin Panel
              </Button>
            ) : userRole === 'manager' && (
              <Button variant="secondary" onClick={() => navigate('/admin')} className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Manage Team
              </Button>
            )}
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Engagement Pulse Section - Only for users with pulse check enabled */}
        {isCurrentUserUpForPulseCheck && (
          <Card className={`mb-8 border-2 ${pulseCheckCompleted ? 'border-green-200 bg-gradient-to-r from-green-50 to-transparent' : 'border-primary/20 bg-gradient-to-r from-primary/5 to-transparent'}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <div>
                    <CardTitle className="text-xl">Your Engagement Pulse Check</CardTitle>
                    <CardDescription>
                      {pulseCheckCompleted 
                        ? 'Thank you for completing your self-reflection survey'
                        : 'Complete your self-reflection survey as part of your review cycle'
                      }
                    </CardDescription>
                  </div>
                </div>
                {pulseCheckCompleted ? (
                  <Button 
                    variant="secondary"
                    disabled
                    className="flex items-center gap-2 bg-green-100 text-green-700 hover:bg-green-100"
                  >
                    <Check className="h-4 w-4" />
                    Completed {pulseCheckCompletedDate ? `- ${pulseCheckCompletedDate.toLocaleDateString()}` : ''}
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate('/pulse-check')} 
                    className="flex items-center gap-2"
                  >
                    Start Survey
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Self-reflection questions</span>
                <span>•</span>
                <span>~3 minutes</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold">Peer Reviews</h2>
            </div>
            {(isAdmin || isHR || userRole === 'manager') && (
              <Button variant="outline" onClick={() => navigate('/admin/reviews')} className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                View Team Reviews
              </Button>
            )}
          </div>
          <p className="text-muted-foreground">
            Click on a team member below to start their peer review. 
            {usersForReview.length === 0 ? ' No one is currently up for review.' : ` ${usersForReview.length} team member${usersForReview.length > 1 ? 's' : ''} up for review.`}
          </p>
        </div>

        {/* Users Up for Review */}
        {usersForReview.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Active Reviews</h3>
              <p className="text-muted-foreground mb-4">
                There are currently no team members up for peer review.
              </p>
              <div className="flex gap-2 justify-center">
                {(isAdmin || isHR) && (
                  <Button onClick={() => navigate('/admin/users')} variant="outline">
                    Manage Review Status
                  </Button>
                )}
                {(isAdmin || isHR || userRole === 'manager') && (
                  <Button onClick={() => navigate('/admin/reviews')} variant="secondary">
                    View Team Reviews
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {usersForReview.map((user) => (
              <Card 
                key={user.id} 
                className={`transition-all duration-200 border-2 ${
                  user.hasCompletedReview 
                    ? 'opacity-75 cursor-default border-border' 
                    : 'cursor-pointer hover:shadow-lg hover:scale-105 hover:border-primary/20'
                }`}
                onClick={() => {
                  if (!user.hasCompletedReview) {
                    handleStartReview(user.id);
                  }
                }}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-4">
                    {user.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt={user.displayName} 
                        className="w-16 h-16 rounded-full object-cover" 
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center"
                      style={{ display: user.photoURL ? 'none' : 'flex' }}
                    >
                      <span className="text-primary font-semibold text-xl">
                        {user.displayName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{user.displayName}</CardTitle>
                      <CardDescription className="text-sm">{user.email}</CardDescription>
                      <div className="flex items-center gap-2 mt-2">
                        {user.hasCompletedReview ? (
                          <Badge variant="secondary" className="text-blue-700">
                            <Check className="h-3 w-3 mr-1" />
                            Review Completed
                          </Badge>
                        ) : (
                          <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                            Up for Peer Review
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {user.managers && user.managers.length > 0 && (
                    <p className="text-xs text-muted-foreground mb-3">
                      Reports to: {user.managers.map(m => m.name).join(', ')}
                    </p>
                  )}
                  {user.reviewCycle && (
                    <p className="text-xs text-muted-foreground mb-3">
                      Review cycle: {user.reviewCycle}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <Button 
                      size="sm" 
                      className="w-full flex items-center gap-2"
                      variant={user.hasCompletedReview ? "secondary" : "default"}
                      disabled={user.hasCompletedReview}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!user.hasCompletedReview) {
                          handleStartReview(user.id);
                        }
                      }}
                    >
                      {user.hasCompletedReview ? (
                        <>
                          <Check className="h-4 w-4" />
                          Review Submitted
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4" />
                          Start Review
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      </main>
    </div>
  );
};

export default Dashboard;
